import { useMemo } from 'react';

import de from 'date-fns/locale/de';
import { get } from 'lodash-es';

import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { dateInitFormats, parseCustomFormatToDate } from '@common/Util';

const StyleDatePicker = styled(DatePicker)(({ theme }) => ({
  fontSize: 14,
  width: '100%',
  '& .MuiInputBase-root': { background: theme.palette.booking.backgroundField },
  '& .MuiSvgIcon-root': { color: theme.palette.primary.light }
}));

function getBrowserLang() {
  const lang =
    (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || 'en';
  return lang.toLowerCase().startsWith('de') ? 'de' : 'en';
}

const Datepicker = ({
  field = {},
  form: { touched, errors } = {},
  required = false,
  isManualInputDisabled = false,
  isHelperTextVisible = true,
  maxDate = null,
  minDate = null,
  onChange = () => {},
  ...rest
}) => {
  const isError = Boolean(get(touched, field?.name) && get(errors, field?.name));
  const errorMsg = get(touched, field?.name) && get(errors, field?.name);

  const browserLang = useMemo(() => getBrowserLang(), []);
  const locale = browserLang === 'de' ? de : undefined;

  const dateParsed = parseCustomFormatToDate(field?.value || rest?.value, [
    dateInitFormats.serverResponseDateFormat,
    dateInitFormats.datePickerFormat
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <StyleDatePicker
        maxDate={maxDate}
        minDate={minDate}
        format={dateInitFormats.dmyFormat}
        slotProps={{
          day: { sx: { '&.Mui-selected': { color: '#fff' } } },
          textField: (props) => ({
            label: rest?.label || '',
            readOnly: isManualInputDisabled,
            onBlur: field?.onBlur,
            required: required,
            name: field?.name,
            size: 'small',
            error: isError,
            helperText: isHelperTextVisible && errorMsg,
            InputProps: { ...props.inputProps, ...props.InputProps },
            onKeyDown: (e) => {
              isManualInputDisabled && e.preventDefault();
            },
            FormHelperTextProps: { ['data-test-id']: field?.name }
          })
        }}
        {...rest}
        onChange={onChange}
        value={dateParsed}
      />
    </LocalizationProvider>
  );
};

export default Datepicker;
